import { useCallback, useEffect, useRef, useState } from 'react';
import { downloads, isMultiPart, isUrlExpired, type DownloadJob } from '../api/downloads';
import type { DownloadPart, DownloadRequest } from '../types/roll';

/** How often a job that's still being packaged is checked. */
export const POLL_MS = 3000;

/**
 * Gap between automatic part downloads. Without it the browser drops some of
 * several back-to-back downloads.
 */
export const PART_DOWNLOAD_DELAY_MS = 800;

export interface DownloadJobState {
  /** A job is being started, packaged, or a failed part rebuilt. */
  preparing: boolean;
  /**
   * The job, once there's something to show for it: every poll of a
   * multi-part job (so its parts show live), or the finished single ZIP.
   */
  job: DownloadJob | null;
  error: string | null;
  /** Where the automatic run of part downloads has got to, while it runs. */
  progress: { current: number; total: number } | null;
}

const IDLE: DownloadJobState = { preparing: false, job: null, error: null, progress: null };

/**
 * Download through a temporary anchor rather than `window.location`, so
 * several can fire in a row without a later one cancelling an earlier one.
 */
function triggerDownload(url: string, filename?: string): void {
  const anchor = document.createElement('a');
  anchor.href = url;
  if (filename) anchor.download = filename;
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
}

function failureMessage(job: DownloadJob): string {
  const failed = job.scan_errors?.length ?? 0;
  if (failed > 0) {
    return `We couldn't prepare your download: ${failed} ${failed === 1 ? 'scan' : 'scans'} could not be packaged.`;
  }
  return "We couldn't prepare your download.";
}

/**
 * Runs one customer download at a time: starts the ZIP job, polls it until
 * it's packaged, then downloads the result. A multi-part job's parts download
 * one after another, and a part that failed can be rebuilt on its own.
 *
 * Polls are chained, never overlapping, and each run carries a generation
 * number so a superseded run (a new download, a dismissal, unmounting) stops
 * at its next step instead of writing over the current one.
 */
export function useDownloadJob() {
  const [state, setState] = useState<DownloadJobState>(IDLE);
  const generation = useRef(0);
  const pollTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const sequenceTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const lastRequest = useRef<DownloadRequest | null>(null);
  // Parts of the current job already downloaded automatically, so rebuilding
  // one failed part doesn't download every other part a second time.
  const delivered = useRef(new Set<number>());

  /** Stop whatever run is in flight and return the new run's generation. */
  const supersede = useCallback(() => {
    generation.current += 1;
    clearTimeout(pollTimer.current);
    clearTimeout(sequenceTimer.current);
    return generation.current;
  }, []);

  useEffect(() => () => void supersede(), [supersede]);

  const deliverParts = useCallback((parts: DownloadPart[], gen: number) => {
    const ready = parts
      .filter((p) => p.status === 'COMPLETE' && p.presigned_url && !delivered.current.has(p.part_no))
      .sort((a, b) => a.part_no - b.part_no);
    let index = 0;
    const next = () => {
      if (gen !== generation.current) return;
      if (index >= ready.length) {
        setState((s) => ({ ...s, progress: null }));
        return;
      }
      const part = ready[index];
      index += 1;
      delivered.current.add(part.part_no);
      setState((s) => ({ ...s, progress: { current: index, total: ready.length } }));
      triggerDownload(part.presigned_url!, part.zip_filename);
      // Wait after the last one too, so "part N of N" is readable before it clears.
      sequenceTimer.current = setTimeout(next, PART_DOWNLOAD_DELAY_MS);
    };
    next();
  }, []);

  /** Poll a job until it's packaged, then deliver it. */
  const track = useCallback(
    async (jobId: string, gen: number) => {
      for (;;) {
        await new Promise<void>((resolve) => {
          pollTimer.current = setTimeout(resolve, POLL_MS);
        });
        if (gen !== generation.current) return;

        let job: DownloadJob;
        try {
          job = await downloads.status(jobId);
        } catch {
          if (gen === generation.current) {
            setState((s) => ({ ...s, preparing: false, error: "We couldn't check on your download." }));
          }
          return;
        }
        if (gen !== generation.current) return;

        if (job.status === 'PENDING' || job.status === 'PROCESSING') {
          if (isMultiPart(job)) setState((s) => ({ ...s, job }));
          continue;
        }

        if (isMultiPart(job)) {
          // Any mix of finished and failed parts: list them all, download the
          // finished ones, and let the customer rebuild the failed ones.
          setState((s) => ({ ...s, preparing: false, job, error: null }));
          deliverParts(job.parts!, gen);
          return;
        }

        if (job.status !== 'COMPLETE') {
          // FAILED, or PARTIAL on a job from before downloads were split into
          // parts, which has nothing to show for the scans that did package.
          setState({ ...IDLE, error: failureMessage(job) });
          return;
        }

        let ready = job;
        if (!job.presigned_url || isUrlExpired(job.presigned_expires_at)) {
          try {
            const refreshed = await downloads.refresh(jobId);
            ready = { ...job, presigned_url: refreshed.presigned_url, presigned_expires_at: refreshed.presigned_expires_at };
          } catch {
            // Leave the link empty; "Download again" refreshes it on demand.
          }
          if (gen !== generation.current) return;
        }
        if (ready.presigned_url) triggerDownload(ready.presigned_url);
        // Keep the job so a download the browser blocked or lost can be
        // fetched again without rebuilding the ZIP.
        setState({ ...IDLE, job: ready });
        return;
      }
    },
    [deliverParts],
  );

  const start = useCallback(
    async (request: DownloadRequest) => {
      const gen = supersede();
      lastRequest.current = request;
      delivered.current = new Set();
      setState({ ...IDLE, preparing: true });
      let jobId: string;
      try {
        ({ job_id: jobId } = await downloads.start(request));
      } catch {
        if (gen === generation.current) setState({ ...IDLE, error: "We couldn't start your download." });
        return;
      }
      if (gen !== generation.current) return;
      await track(jobId, gen);
    },
    [supersede, track],
  );

  /** Build the last download again from scratch. */
  const retry = useCallback(async () => {
    if (lastRequest.current) await start(lastRequest.current);
  }, [start]);

  /** Download a finished single-part job again, re-signing an expired link. */
  const downloadAgain = useCallback(async () => {
    const job = state.job;
    if (!job) return;
    setState((s) => ({ ...s, error: null }));
    try {
      let url = job.presigned_url;
      if (!url || isUrlExpired(job.presigned_expires_at)) {
        const refreshed = await downloads.refresh(job.job_id);
        url = refreshed.presigned_url;
        setState((s) =>
          s.job?.job_id === job.job_id
            ? { ...s, job: { ...s.job, presigned_url: url, presigned_expires_at: refreshed.presigned_expires_at } }
            : s,
        );
      }
      if (url) triggerDownload(url);
    } catch {
      setState((s) => ({ ...s, error: "We couldn't fetch your download. Please try again." }));
    }
  }, [state.job]);

  /** Download one finished part, re-signing an expired link. */
  const downloadPart = useCallback(
    async (part: DownloadPart) => {
      const job = state.job;
      if (!job) return;
      setState((s) => ({ ...s, error: null }));
      try {
        let url = part.presigned_url;
        if (!url || isUrlExpired(part.presigned_expires_at)) {
          const refreshed = await downloads.refresh(job.job_id);
          if (isMultiPart(refreshed)) {
            setState((s) => (s.job?.job_id === job.job_id ? { ...s, job: refreshed } : s));
          }
          url = refreshed.parts?.find((p) => p.part_no === part.part_no)?.presigned_url ?? refreshed.presigned_url;
        }
        if (url) triggerDownload(url, part.zip_filename);
      } catch {
        setState((s) => ({ ...s, error: `We couldn't fetch ${part.label}. Please try again.` }));
      }
    },
    [state.job],
  );

  /** Rebuild one failed part, then download it when it's ready. */
  const retryPart = useCallback(
    async (part: DownloadPart) => {
      const job = state.job;
      if (!job) return;
      const gen = supersede();
      setState((s) => ({
        ...s,
        preparing: true,
        error: null,
        progress: null,
        job: s.job && {
          ...s.job,
          parts: s.job.parts?.map((p) => (p.part_no === part.part_no ? { ...p, status: 'PENDING' } : p)),
        },
      }));
      try {
        await downloads.retryPart(job.job_id, part.part_no);
      } catch {
        if (gen === generation.current) {
          setState((s) => ({ ...s, preparing: false, job, error: `We couldn't rebuild ${part.label}. Please try again.` }));
        }
        return;
      }
      if (gen !== generation.current) return;
      await track(job.job_id, gen);
    },
    [state.job, supersede, track],
  );

  /** Put the panel away, abandoning any job still being prepared. */
  const dismiss = useCallback(() => {
    supersede();
    setState(IDLE);
  }, [supersede]);

  return { ...state, start, retry, downloadAgain, downloadPart, retryPart, dismiss };
}

export type DownloadJobControls = ReturnType<typeof useDownloadJob>;
